/**
 * ArticleDetailPage — public single article view.
 *
 * Features:
 *  - Full article content with featured image hero
 *  - Like button (persists per user in Firestore, anonymous users get a local key)
 *  - View count (incremented once per session, shown only when showViewCount is true)
 *  - Social share (Web Share API + Twitter/X, LinkedIn, copy-link fallbacks)
 *  - Comments section (when commentsEnabled is true — pending moderation shown to commenter)
 */

import { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Heart,
  Eye,
  Clock,
  Calendar,
  Share2,
  Twitter,
  Linkedin,
  Link2,
  Check,
  ChevronLeft,
  MessageSquare,
  Send,
  BookOpen,
  User,
} from 'lucide-react';
import { auth } from '@/lib/firebase';
import {
  getPublishedArticleBySlug,
  incrementViewCount,
  toggleLike,
  hasUserLiked,
  getApprovedComments,
  addComment,
  estimateReadTime,
  formatDate,
  getAnonymousUserId,
  type BlogArticle,
  type BlogComment,
} from '@/lib/blog';

/**
 * Prepare article body HTML for public display.
 * TipTap already outputs correct HTML (<br> for Shift+Enter, <p> for Enter).
 * We only strip runs of empty <p></p> to avoid large blank gaps between sections.
 * We do NOT convert \n → <br> because \n is just HTML source whitespace between
 * tags — doing so adds a <br> between every <li>, <p>, etc. and creates huge gaps.
 */
function bodyHtmlForDisplay(html: string): string {
  if (!html) return '';
  // Collapse two or more consecutive empty <p></p> to a single one
  return html.replace(/(<p[^>]*>\s*<\/p>\s*){2,}/gi, '<p></p>');
}

export function ArticleDetailPage(): JSX.Element {
  const { slug } = useParams<{ slug: string }>();

  const [article, setArticle] = useState<BlogArticle | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  // Likes
  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const [liking, setLiking] = useState(false);

  // Social share
  const [copied, setCopied] = useState(false);

  // Comments
  const [comments, setComments] = useState<BlogComment[]>([]);
  const [commentForm, setCommentForm] = useState({
    authorName: '',
    authorEmail: '',
    body: '',
  });
  const [commentErrors, setCommentErrors] = useState<Partial<typeof commentForm>>({});
  const [submittingComment, setSubmittingComment] = useState(false);
  const [commentSubmitted, setCommentSubmitted] = useState(false);
  const [viewCount, setViewCount] = useState(0);

  // ---------------------------------------------------------------------------
  // Load article
  // ---------------------------------------------------------------------------

  const loadArticle = useCallback(async () => {
    if (!slug) return;
    setLoading(true);
    try {
      const data = await getPublishedArticleBySlug(slug);
      if (!data) {
        setNotFound(true);
        return;
      }
      setArticle(data);
      setLikeCount(data.likeCount ?? 0);
      setViewCount(data.viewCount ?? 0);

      // Load approved comments
      if (data.commentsEnabled) {
        const cmts = await getApprovedComments(data.id);
        setComments(cmts);
      }

      // Check if current user has liked
      const userId = auth.currentUser?.uid ?? getAnonymousUserId();
      const alreadyLiked = await hasUserLiked(data.id, userId);
      setLiked(alreadyLiked);
    } catch (err) {
      console.error('[ArticleDetailPage] Failed to load article:', err);
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    loadArticle();
  }, [loadArticle]);

  // Increment view count once per session after article loads
  useEffect(() => {
    if (!article) return;
    incrementViewCount(article.id)
      .then(() => setViewCount((c) => c + 1))
      .catch(() => {});
  }, [article]);

  // ---------------------------------------------------------------------------
  // Like
  // ---------------------------------------------------------------------------

  async function handleLike() {
    if (!article || liking) return;
    setLiking(true);
    try {
      const userId = auth.currentUser?.uid ?? getAnonymousUserId();
      const nowLiked = await toggleLike(article.id, userId);
      setLiked(nowLiked);
      setLikeCount((c) => (nowLiked ? c + 1 : Math.max(0, c - 1)));
    } catch (err) {
      console.error('[ArticleDetailPage] Like failed:', err);
    } finally {
      setLiking(false);
    }
  }

  // ---------------------------------------------------------------------------
  // Social share
  // ---------------------------------------------------------------------------

  const articleUrl = typeof window !== 'undefined' ? window.location.href : '';

  async function handleShare() {
    if (navigator.share) {
      try {
        await navigator.share({
          title: article?.title ?? 'Article',
          text: article?.excerpt ?? '',
          url: articleUrl,
        });
      } catch {
        // User dismissed share sheet — no-op
      }
    }
  }

  function handleCopyLink() {
    navigator.clipboard.writeText(articleUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  // ---------------------------------------------------------------------------
  // Comments
  // ---------------------------------------------------------------------------

  function validateComment(): boolean {
    const errors: Partial<typeof commentForm> = {};
    if (!commentForm.authorName.trim()) errors.authorName = 'Name is required';
    if (!commentForm.body.trim()) errors.body = 'Comment cannot be empty';
    setCommentErrors(errors);
    return Object.keys(errors).length === 0;
  }

  async function handleSubmitComment(e: React.FormEvent) {
    e.preventDefault();
    if (!article || !validateComment()) return;
    setSubmittingComment(true);
    try {
      await addComment(article.id, {
        authorName: commentForm.authorName.trim(),
        authorEmail: commentForm.authorEmail.trim() || null,
        body: commentForm.body.trim(),
      });
      setCommentForm({ authorName: '', authorEmail: '', body: '' });
      setCommentSubmitted(true);
    } catch (err) {
      console.error('[ArticleDetailPage] Comment submission failed:', err);
    } finally {
      setSubmittingComment(false);
    }
  }

  // ---------------------------------------------------------------------------
  // Loading / Not found
  // ---------------------------------------------------------------------------

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
      </div>
    );
  }

  if (notFound || !article) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center px-4 text-center">
        <BookOpen size={48} className="text-gray-300 mb-4" />
        <h1 className="text-2xl font-bold text-gray-800">Article not found</h1>
        <p className="mt-2 text-gray-500">
          This article may have been removed or the link is incorrect.
        </p>
        <Link
          to="/articles"
          className="mt-6 inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 transition-colors"
        >
          <ChevronLeft size={15} />
          Back to all articles
        </Link>
      </div>
    );
  }

  const readTime = estimateReadTime(article.body);
  const updatedAtDate =
    article.updatedAt instanceof Date
      ? article.updatedAt
      : (article.updatedAt as { toDate?: () => Date })?.toDate?.() ?? new Date();
  const publishedAtDate =
    article.publishedAt instanceof Date
      ? article.publishedAt
      : (article.publishedAt as { toDate?: () => Date })?.toDate?.() ?? new Date();
  const wasUpdated =
    article.publishedAt && Math.abs(updatedAtDate.getTime() - publishedAtDate.getTime()) > 60000;

  const twitterShareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(articleUrl)}&text=${encodeURIComponent(article.title)}`;
  const linkedinShareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(articleUrl)}`;

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Featured image hero */}
      {article.featuredImage && (
        <div className="w-full max-h-[480px] overflow-hidden bg-gray-900">
          <img
            src={article.featuredImage}
            alt={article.title}
            className="w-full h-full object-cover opacity-90"
          />
        </div>
      )}

      {/* Article content container */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* Back link */}
        <Link
          to="/articles"
          className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-blue-600 transition-colors mb-6 group"
        >
          <ChevronLeft size={15} className="group-hover:-translate-x-0.5 transition-transform" />
          Back to all articles
        </Link>

        {/* Tags */}
        {article.tags?.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-4">
            {article.tags.map((tag) => (
              <span
                key={tag}
                className="rounded-full bg-blue-50 px-3 py-0.5 text-xs font-medium text-blue-700"
              >
                {tag}
              </span>
            ))}
          </div>
        )}

        {/* Title */}
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 leading-tight">
          {article.title}
        </h1>

        {/* Meta bar */}
        <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-gray-500 pb-4 border-b border-gray-200">
          {/* Author */}
          <span className="inline-flex items-center gap-1.5 font-medium text-gray-700">
            <User size={14} />
            {article.authorName}
          </span>
          {/* Publish date */}
          <span className="inline-flex items-center gap-1">
            <Calendar size={13} />
            {formatDate(article.publishedAt)}
          </span>
          {/* Updated date (shown only when meaningfully different from publish) */}
          {wasUpdated && (
            <span className="inline-flex items-center gap-1 text-xs text-gray-400">
              Updated {formatDate(article.updatedAt)}
            </span>
          )}
          {/* Read time */}
          <span className="inline-flex items-center gap-1">
            <Clock size={13} />
            {readTime} min read
          </span>
          {/* View count (shown when enabled) */}
          {article.showViewCount && (
            <span className="inline-flex items-center gap-1">
              <Eye size={13} />
              {viewCount} {viewCount === 1 ? 'view' : 'views'}
            </span>
          )}
        </div>

        {/* Article body — prose with tight spacing; no whitespace-pre-wrap (that breaks list gap) */}
        <div
          className="mt-8 prose prose-gray prose-a:text-blue-600 hover:prose-a:text-blue-700 prose-img:rounded-xl prose-blockquote:border-blue-400 max-w-none [&_p]:my-2 [&_li]:my-0.5 [&_ul]:my-2 [&_ol]:my-2 [&_h2]:text-2xl [&_h2]:font-semibold [&_h2]:mt-6 [&_h2]:mb-2 [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:mt-4 [&_h3]:mb-1"
          dangerouslySetInnerHTML={{ __html: bodyHtmlForDisplay(article.body) }}
        />

        {/* Like + Share bar */}
        <div className="mt-10 pt-6 border-t border-gray-200 flex flex-wrap items-center justify-between gap-4">
          {/* Like button */}
          <button
            type="button"
            onClick={handleLike}
            disabled={liking}
            aria-label={liked ? 'Unlike this article' : 'Like this article'}
            className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-all focus:outline-none focus:ring-2 focus:ring-offset-2 ${
              liked
                ? 'bg-red-50 text-red-600 border border-red-200 hover:bg-red-100 focus:ring-red-400'
                : 'bg-gray-100 text-gray-600 border border-gray-200 hover:bg-gray-200 focus:ring-gray-400'
            } disabled:opacity-60`}
          >
            <Heart
              size={16}
              className={`transition-transform ${liked ? 'fill-current scale-110' : ''}`}
            />
            <span>{likeCount}</span>
            <span className="hidden sm:inline">{liked ? 'Liked' : 'Like'}</span>
          </button>

          {/* Share buttons */}
          <div className="flex items-center gap-2">
            {/* Native share (mobile) — show only when supported */}
            {typeof navigator !== 'undefined' && 'share' in navigator && (
              <button
                type="button"
                onClick={handleShare}
                className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                aria-label="Share article"
              >
                <Share2 size={15} />
                <span className="hidden sm:inline">Share</span>
              </button>
            )}
            {/* Twitter/X */}
            <a
              href={twitterShareUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center w-9 h-9 rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-[#1DA1F2] hover:text-white hover:border-[#1DA1F2] transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
              aria-label="Share on Twitter"
            >
              <Twitter size={14} />
            </a>
            {/* LinkedIn */}
            <a
              href={linkedinShareUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center w-9 h-9 rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-[#0A66C2] hover:text-white hover:border-[#0A66C2] transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
              aria-label="Share on LinkedIn"
            >
              <Linkedin size={14} />
            </a>
            {/* Copy link */}
            <button
              type="button"
              onClick={handleCopyLink}
              className="inline-flex items-center justify-center w-9 h-9 rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
              aria-label="Copy link"
            >
              {copied ? <Check size={14} className="text-green-600" /> : <Link2 size={14} />}
            </button>
          </div>
        </div>

        {/* ----------------------------------------------------------------
            Author bio card
        ---------------------------------------------------------------- */}
        <div className="mt-8 rounded-xl border border-gray-200 bg-white p-5 flex items-start gap-4">
          <div className="w-12 h-12 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0">
            <span className="text-white font-semibold text-lg">
              {article.authorName.charAt(0).toUpperCase()}
            </span>
          </div>
          <div>
            <p className="font-semibold text-gray-900">{article.authorName}</p>
            <p className="text-sm text-gray-500 mt-0.5">
              SMBPrime contributor — helping small businesses navigate federal contracting.
            </p>
          </div>
        </div>

        {/* ----------------------------------------------------------------
            Comments section
        ---------------------------------------------------------------- */}
        {article.commentsEnabled && (
          <div className="mt-12">
            <div className="flex items-center gap-2 mb-6">
              <MessageSquare size={20} className="text-gray-600" />
              <h2 className="text-xl font-semibold text-gray-900">
                Comments
                {comments.length > 0 && (
                  <span className="ml-2 text-sm font-normal text-gray-400">
                    ({comments.length})
                  </span>
                )}
              </h2>
            </div>

            {/* Existing comments */}
            {comments.length > 0 && (
              <div className="space-y-4 mb-8">
                {comments.map((comment) => (
                  <div
                    key={comment.id}
                    className="rounded-xl border border-gray-200 bg-white p-4"
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
                        <span className="text-gray-600 text-xs font-semibold">
                          {comment.authorName.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{comment.authorName}</p>
                        <p className="text-xs text-gray-400">{formatDate(comment.createdAt)}</p>
                      </div>
                    </div>
                    <p className="text-sm text-gray-700 leading-relaxed ml-10">
                      {comment.body}
                    </p>
                  </div>
                ))}
              </div>
            )}

            {/* Comment submission form */}
            {commentSubmitted ? (
              <div className="rounded-xl border border-green-200 bg-green-50 p-5 text-center">
                <Check size={24} className="text-green-600 mx-auto mb-2" />
                <p className="font-medium text-green-800">Comment submitted!</p>
                <p className="text-sm text-green-700 mt-1">
                  Your comment is pending review and will appear once approved.
                </p>
              </div>
            ) : (
              <form
                onSubmit={handleSubmitComment}
                className="rounded-xl border border-gray-200 bg-white p-5 space-y-4"
              >
                <h3 className="font-semibold text-gray-900">Leave a comment</h3>
                <p className="text-xs text-gray-500">
                  Comments are reviewed before appearing publicly.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">
                      Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={commentForm.authorName}
                      onChange={(e) =>
                        setCommentForm((f) => ({ ...f, authorName: e.target.value }))
                      }
                      placeholder="Your name"
                      className={`w-full rounded-lg border px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 ${
                        commentErrors.authorName ? 'border-red-400' : 'border-gray-300'
                      }`}
                    />
                    {commentErrors.authorName && (
                      <p className="text-xs text-red-500 mt-1">{commentErrors.authorName}</p>
                    )}
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">
                      Email (optional)
                    </label>
                    <input
                      type="email"
                      value={commentForm.authorEmail}
                      onChange={(e) =>
                        setCommentForm((f) => ({ ...f, authorEmail: e.target.value }))
                      }
                      placeholder="your@email.com"
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">
                    Comment <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={commentForm.body}
                    onChange={(e) => setCommentForm((f) => ({ ...f, body: e.target.value }))}
                    placeholder="Share your thoughts…"
                    rows={4}
                    className={`w-full rounded-lg border px-3 py-2 text-sm resize-none focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 ${
                      commentErrors.body ? 'border-red-400' : 'border-gray-300'
                    }`}
                  />
                  {commentErrors.body && (
                    <p className="text-xs text-red-500 mt-1">{commentErrors.body}</p>
                  )}
                </div>
                <button
                  type="submit"
                  disabled={submittingComment}
                  className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-60 transition-colors"
                >
                  {submittingComment ? (
                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  ) : (
                    <Send size={14} />
                  )}
                  {submittingComment ? 'Submitting…' : 'Submit Comment'}
                </button>
              </form>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
